import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { TextButton } from "@/modules/common/components/ui/text-button";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { useEditPropertyFinishingInfo } from "@/modules/property-tracks/hooks/financial-info/edit-financial-info.hook";
import { Property } from "@/modules/property-tracks/types";
import { editFinancialInfoSchema } from "@/modules/property-tracks/utils/financial-info";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilLine } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import EditFinancialInfoForm from "./edit-financial-info-form";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import { Box, Stack } from "@mui/material";

type EditFinancialInfoFormFields = z.infer<
  ReturnType<typeof editFinancialInfoSchema>
>;

export default function EditFinancialInfo({
  property,
}: Readonly<{
  property: Property;
}>) {
  const { isMobile } = useScreenSize();
  const [modalOpen, setModalOpen] = useState(false);

  const defaultValues: EditFinancialInfoFormFields = {
    contractSellingPrice:
      property.propertyFinishing?.contractSellingPrice ?? undefined,
    actualSellingPrice:
      property.propertyFinishing?.actualSellingPrice ?? undefined,
    forcastedRentalUnits: property.propertyFinishing?.forcastedRentalUnits ?? 1,
    rentalTag: property.propertyFinishing?.rentalTag ?? undefined,
    hospitalityType: property.hospitalityType ?? undefined,
  };

  const { handleSubmit, reset, control, setValue } =
    useForm<EditFinancialInfoFormFields>({
      resolver: zodResolver(editFinancialInfoSchema()),
    });

  const onSubmit: SubmitHandler<EditFinancialInfoFormFields> = async (data) => {
    submitEditPropertyFinishing(data);
  };

  const mutation = useEditPropertyFinishingInfo();

  const submitEditPropertyFinishing = (data: EditFinancialInfoFormFields) => {
    mutation.mutate(
      {
        uid: property.uid,
        data: {
          actualSellingPrice: data.actualSellingPrice,
          contractSellingPrice: data.contractSellingPrice,
          forcastedRentalUnits: data.forcastedRentalUnits,
          rentalTag: data.rentalTag,
          hospitalityType: data.hospitalityType,
        },
      },
      {
        onSuccess: () => {
          setModalOpen(false);
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Property Financial Info updated successfully",
            toastId: TOAST_IDS.EDIT_FINANCIAL_INFO_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_FINANCIAL_INFO_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    reset(defaultValues);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <TextButton
        disabled={readonly}
        onClick={handleModalOpen}
        sx={{ display: "flex", gap: 0.5, alignItems: "bottom" }}
      >
        <PencilLine size={16} /> Edit
      </TextButton>
      {isMobile ? (
        <CommonBottomSheet
          isOpen={modalOpen}
          onClose={handleCloseModal}
          snapPoints={[0.65, 0.8]}
        >
          <ModalHeader title="Edit Financial Info" onClose={handleCloseModal} />
          <Box>
            <Stack gap={1} p={2}>
              <EditFinancialInfoForm control={control} setValue={setValue} />
            </Stack>
            <Box position="sticky" bottom={0}>
              <ModalFooter
                confirmButtonText="Save"
                cancelButtonText="Cancel"
                onConfirm={handleSubmit(onSubmit)}
                isLoading={mutation.isPending}
                onCancel={handleCloseModal}
              />
            </Box>
          </Box>
        </CommonBottomSheet>
      ) : (
        <EditFieldModal
          open={modalOpen}
          setOpen={setModalOpen}
          header="Edit Financial Info"
          width={480}
          handleConfirm={handleSubmit(onSubmit)}
          handleCloseModal={handleCloseModal}
          isLoading={mutation.isPending}
        >
          <EditFinancialInfoForm control={control} setValue={setValue} />
        </EditFieldModal>
      )}
    </>
  );
}
